import React, { Fragment, useState, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Card, Row, Col, Tag, Spin, Skeleton } from "antd";
import { DashboardHeader } from "../components/top-bar/Dashboard-header";
import { useLocation } from "react-router-dom";
import { ParticipantperMonth } from "../components/executive-summary/Particpants-per-Month";
import { ShopsalesperMonth } from "../components/executive-summary/Shop-sales-per-month";
import { BoothSalesperMonth } from "../components/executive-summary/Booth-Sales-per-Month";
import { useGetDashboardExctuiveSummaryData } from "../use-cases/get-exctuive-summary";
import dayjs from "dayjs";
import { useGetDashboardCardData } from "../use-cases/get-cardDashBoard";
import graph from "assets/Dashboard/graph.svg";
import cash from "assets/Dashboard/Money.svg";

const monthShortNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"
];
const monthFullNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export const ExecutiveSummary: React.FC = () => {
  const today = dayjs();
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const location = useLocation();
  const pathnameWithoutDashboard = location.pathname.replace("/dashboard/", "");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>('' as any);
  // [
  //   today,
  //   today,
  // ]
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [selectedYear, setSelectedYear] = useState<number>(today.year());
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const barColors = [
    '#E55050',
    '#008048',
    '#35B1E8',
    '#FFA51F',
    '#FEBEC8',
    '#4C5167',
    '#008044',
    '#35B1E8',
  ];;
  const monthListPayload = useMemo(() => {
    return selectedMonths
      .map((shortMonth) => monthShortNames.indexOf(shortMonth) + 1)
      .join(",");
  }, [selectedMonths]);

  const { data: SalesDataGraph, isFetching: SalesDataisLoading, refetch } =
    useGetDashboardExctuiveSummaryData({
      Flag: "salesdata",
      FromDate: dateRange?.[0]?.format("YYYY-MM-DD") || "",
      ToDate: dateRange?.[1]?.format("YYYY-MM-DD") || "",
      monthlist: monthListPayload || undefined,
      Year: selectedYear,
      EventId: selectedEvent,
      CategoryId: selectedCategory,
    } as any);

  const { data: cardData, isLoading } = useGetDashboardCardData({
    FromDate: dateRange?.[0]?.format("YYYY-MM-DD") || "",
    ToDate: dateRange?.[1]?.format("YYYY-MM-DD") || "",
    Year: selectedYear,
    EventId: selectedEvent,
    CategoryId: selectedCategory,
  });

  // ✅ Control Highcharts loading overlay
  // useEffect(() => {
  //   const chart = chartRef.current?.chart;
  //   if (chart) {
  //     if (SalesDataisLoading) {
  //       chart.showLoading("Loading data...");
  //     } else {
  //       chart.hideLoading();
  //     }
  //   }
  // }, [SalesDataisLoading]);

  const handleMonthToggle = (month: string) => {
    setSelectedMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  };

  const monthlyDataFromApi =
    SalesDataGraph?.GraphData?.map((item: any) => ({
      name: monthShortNames[item.GMonth - 1],
      y: item.TotalCount,
      revenue: item?.TotalRevenue,
      drilldown: monthFullNames[item.GMonth - 1],
    })) || [];

  const drilldownSeriesFromApi =
    SalesDataGraph?.DrilldownData
      ? Object.values(
        SalesDataGraph.DrilldownData.reduce((acc: any, day: any) => {
          const monthFull = monthFullNames[day.GMonth - 1];
          if (!acc[monthFull]) {
            acc[monthFull] = {
              id: monthFull,
              name: `Daily Sales - ${monthFull}`,
              type: "column",
              colors: barColors,
              data: [],
            };
          }
          // acc[monthFull].data.push([
          //   `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
          //   day.TotalRevenue,
          // ]);
          acc[monthFull].data.push({
            name: `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
            y: day.TotalCount,          // ✅ use count for the column height
            revenue: day.TotalRevenue,  // ✅ custom property for tooltip
          });

          return acc;
        }, {})
      )
      : [];

  const baseSeriesData =
    selectedMonths.length === 0
      ? monthlyDataFromApi
      : selectedMonths.map((shortMonth) => {
        const monthIndex = monthShortNames.indexOf(shortMonth);
        const total =
          SalesDataGraph?.DrilldownData?.filter(
            (d: any) => d.GMonth - 1 === monthIndex
          ).reduce((sum: number, d: any) => sum + d.TotalRevenue, 0) || 0;

        return { name: shortMonth, y: total };
      });

  const totalRevenue =
    SalesDataGraph?.GraphData?.reduce(
      (sum: number, item: any) => sum + (item.TotalRevenue || 0),
      0
    ) || 0;

  const chartOptions: Highcharts.Options = {
    chart: { type: "column" },
    title: {
      text:
        selectedMonths.length === 0
          ? "Registration Sales per Month"
          : `Combined Sales for: ${selectedMonths.join(", ")}`,
    },
    subtitle: {
      text: `Total Revenue: KWD ${totalRevenue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
      })}`,
      style: {
        fontSize: '10px', // 🔹 set your desired font size
        fontWeight: 'bold', // optional: make it bold
        color: '#333', // optional: set color
      },
    },
    xAxis: {
      type: "category",
      labels: {
        style: {
          fontSize: "11px",
          color: "black",        // ✅ month names black
          textDecoration: "none" // ✅ no underline
        },
      },
    },
    yAxis: {
      title: { text: "Sales (KWD)", style: { fontSize: "11px", color: "black" } },
      labels: { style: { fontSize: "11px", color: "black" } },
    },
    legend: { enabled: false },
    colors: barColors, // ✅ Apply alternating colors globally
    credits: { enabled: false },
    plotOptions: {
      series: {
        borderWidth: 0,
        colorByPoint: true, // ✅ Alternate colors for bars
        dataLabels: {
          enabled: true,
          format: "{point.y:.0f}", // ✅ only top value
          style: {
            color: "black",   // ✅ bar top values in black
            fontSize: "11px",
            fontWeight: "bold",
          },
        },
      },
    },
    tooltip: {
      headerFormat: '<span style="font-size:11px">{series.name}</span><br/>',
      pointFormatter: function (this: any) {
        return `
      <span style="color:${this.color}">${this.name}</span><br/>
      <b>Total Count:</b> ${this.y}<br/>
      <b>Total Revenue:</b> KWD ${(this.revenue || 0)}
    `;
      }
    },
    series: [
      {
        name:
          selectedMonths.length === 0
            ? "Monthly Sales"
            : `Total Sales (${selectedMonths.length} month${selectedMonths.length > 1 ? "s" : ""
            })`,
        type: "column",
        data: baseSeriesData,
      },
    ],
    ...(selectedMonths.length === 0 && {
      drilldown: {
        series: drilldownSeriesFromApi,
        activeAxisLabelStyle: {
          textDecoration: "none",
          color: "black"
        },
        activeDataLabelStyle: {
          textDecoration: "none",
          color: "black"
        }
      },
    }),
  } as any;

  return (
    <Fragment>
      <DashboardHeader
        title={pathnameWithoutDashboard}
        setDateRange={setDateRange}
        setSelectedYear={setSelectedYear}
        selectedYear={selectedYear}
        year={selectedYear}
        dateRange={dateRange}
        setSelectedEvent={setSelectedEvent}
        setSelectedCategory={setSelectedCategory}
        selectedEvent={selectedEvent}
        selectedCategory={selectedCategory}
        refetch={refetch}
        chartRef={chartRef}   // 🔹 pass chartRef here
      />

      <Row gutter={[16, 16]} className="row_gap respn_row">
        <Col span={6}>
          <div className="sales_regis">
            <Card style={{ backgroundColor: "#F36363" }}>
              <Spin spinning={isLoading}>


                <p style={{ fontSize: 14, color: "#fff" }}>Total Registration-Sales</p>

                <div className="card_img_t">
                  <h2 style={{ fontSize: 16, color: "#fff" }}>
                    KWD {cardData?.TotalRegistrationSales}
                  </h2>
                  <img src={cash} alt="" width={40} height={40} style={{ filter: "brightness(0) invert(1)" }} />
                </div>
              </Spin>
            </Card>
          </div>
        </Col>
        <Col span={6}>
          <div className="sales_regis">
            <Card style={{ backgroundColor: "#7259FE" }}>
              <Spin spinning={isLoading} style={{ height: "100%", width: "100%" }}>


                <p style={{ fontSize: 14, color: "#fff" }}>Total Participants - Registration</p>

                <div className="card_img_t">
                  <h2 style={{ fontSize: 16, color: "#fff" }}>{cardData?.TotalParticipants}</h2>
                  <img src={graph} alt="" width={40} height={40} style={{ filter: "brightness(0) invert(1)" }} />
                </div>
              </Spin>
            </Card>
          </div>
        </Col>
        <Col span={6}>
          <div className="sales_regis">
            <Card style={{ backgroundColor: "#E3BB39" }}>
              <Spin spinning={isLoading}>


                <p style={{ fontSize: 14, color: "#fff" }}>Total Sales - Shop</p>
                <div className="card_img_t">
                  <h2 style={{ fontSize: 16, color: "#fff" }}>KWD {cardData?.TotalShopSales}</h2>
                  <img src={cash} alt="" width={40} height={40} style={{ filter: "brightness(0) invert(1)" }} />
                </div>
              </Spin>
            </Card>
          </div>
        </Col>
        <Col span={6}>
          <div className="sales_regis">
            <Card style={{ backgroundColor: "#5d2e07ff" }}>
              <Spin spinning={isLoading}>


                <p style={{ fontSize: 14, color: "#fff" }}>ExpoBooth-Sales</p>
                <div className="card_img_t">
                  <h2 style={{ fontSize: 16, color: "#fff" }}>KWD {cardData?.ExpoBoothSales}</h2>
                  <img src={cash} alt="" width={40} height={40} style={{ filter: "brightness(0) invert(1)" }} />
                </div>
              </Spin>
            </Card>
          </div>
        </Col>
      </Row>

      <Row className="graph_row" gutter={16}>
        <Col span={12} className="respn_col row_gap">
          <Card>
            <Row gutter={[8, 8]} className="month_tabs">
              {monthShortNames.map((shortMonth) => (
                <Col key={shortMonth} >
                  <Tag
                    color={
                      selectedMonths.includes(shortMonth) ? "yellow" : "default"
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => handleMonthToggle(shortMonth)}
                  >
                    {shortMonth}
                  </Tag>
                </Col>
              ))}
            </Row>

            {/* <Spin spinning={SalesDataisLoading} > */}
            {SalesDataisLoading ? <Skeleton /> :
              <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                ref={chartRef}
              />}
            {/* </Spin> */}
          </Card>
        </Col>

        <Col span={12} className="respn_col row_gap">
          <ParticipantperMonth dateRange={dateRange} chartRef={chartRef} selectedYear={selectedYear} selectedCategory={selectedCategory} selectedEvent={selectedEvent} />
        </Col>
        <Col span={12} className="row_gap respn_col">
          <ShopsalesperMonth dateRange={dateRange} chartRef={chartRef} selectedYear={selectedYear} selectedCategory={selectedCategory} selectedEvent={selectedEvent} />
        </Col>
        <Col span={12} className="row_gap respn_col">
          <BoothSalesperMonth dateRange={dateRange} chartRef={chartRef} selectedYear={selectedYear} selectedCategory={selectedCategory} selectedEvent={selectedEvent} />
        </Col>
      </Row>
    </Fragment>
  );
};
